/**
 * CPT Proof Map Engine
 * 
 * Deterministic, rule-based matching engine that converts
 * surgeon dictation and op note text into structured evidence
 * for CPT documentation requirements.
 * 
 * NON-NEGOTIABLE:
 * - Never infers "performed" from weak language
 * - Detects negations and contradictions
 * - Stores all evidence snippets for auditability
 * - Every decision is explainable in UI
 */

import {
  CptProofMap,
  EvidenceRule,
  ProofMapRequirementElement,
  EvidenceSource,
  ProofEvidenceSnippet,
  RuleMatchResult,
  RequirementMatchResult,
  CptProofMapResult,
  CaseProofMapResult,
  ProofDocStatus,
  RequirementStatus,
  RuleConfidence,
  ProofMapClarificationTrigger,
} from './types';

// ============================================================================
// SECTION DETECTION
// ============================================================================

const SECTION_PATTERNS: Array<{ pattern: RegExp; name: string }> = [
  { pattern: /(?:preoperative|pre-operative)\s*diagnosis/i, name: 'Preoperative Diagnosis' },
  { pattern: /(?:postoperative|post-operative)\s*diagnosis/i, name: 'Postoperative Diagnosis' },
  { pattern: /procedure(?:s)?\s*(?:performed|details)/i, name: 'Procedure' },
  { pattern: /indications?/i, name: 'Indications' },
  { pattern: /findings?/i, name: 'Findings' },
  { pattern: /implants?\s*(?:used)?/i, name: 'Implants' },
  { pattern: /closure/i, name: 'Closure' },
  { pattern: /anesthesia/i, name: 'Anesthesia' },
  { pattern: /diagnostic\s*arthroscopy/i, name: 'Diagnostic Arthroscopy' },
  { pattern: /estimated\s*blood\s*loss/i, name: 'EBL' },
];

/**
 * Detect which section of the operative note a given offset falls in.
 */
function detectSectionAtOffset(text: string, offset: number): string | undefined {
  let lastSection: string | undefined;
  let lastSectionStart = -1;

  for (const { pattern, name } of SECTION_PATTERNS) {
    const matches = text.matchAll(new RegExp(pattern, 'gi'));
    for (const match of matches) {
      const matchStart = match.index ?? 0;
      if (matchStart <= offset && matchStart > lastSectionStart) {
        lastSection = name;
        lastSectionStart = matchStart;
      }
    }
  }

  return lastSection;
}

// ============================================================================
// TEXT MATCHING
// ============================================================================

/**
 * Find all occurrences of a pattern in text with context.
 */
function findPatternInText(
  text: string,
  pattern: string,
  source: EvidenceSource,
  ruleId: string,
  contextChars: number = 80,
  isNegation: boolean = false,
): ProofEvidenceSnippet[] {
  const snippets: ProofEvidenceSnippet[] = [];
  const lowerText = text.toLowerCase();
  const lowerPattern = pattern.toLowerCase();

  let searchStart = 0;
  while (true) {
    const idx = lowerText.indexOf(lowerPattern, searchStart);
    if (idx === -1) break;

    const snippetStart = Math.max(0, idx - contextChars);
    const snippetEnd = Math.min(text.length, idx + lowerPattern.length + contextChars);

    // Snap to word boundaries
    const adjustedStart = snippetStart > 0
      ? (text.indexOf(' ', snippetStart) + 1) || snippetStart
      : snippetStart;
    const adjustedEnd = snippetEnd < text.length
      ? (text.lastIndexOf(' ', snippetEnd)) || snippetEnd
      : snippetEnd;

    const matchedText = text.slice(adjustedStart, adjustedEnd).trim();
    const sectionHint = detectSectionAtOffset(text, idx);

    snippets.push({
      source,
      matched_text: matchedText,
      offset_start: adjustedStart,
      offset_end: adjustedEnd,
      matched_rule_id: ruleId,
      matched_patterns: [pattern],
      section_hint: sectionHint,
      is_negation: isNegation,
    });

    searchStart = idx + lowerPattern.length;
  }

  return snippets;
}

/**
 * Check context proximity: does the required context term appear
 * within N words of the matched pattern?
 */
function checkContextProximity(
  text: string,
  patternIndex: number,
  patternLength: number,
  contextTerm: string,
  maxDistanceWords: number,
): boolean {
  const lowerText = text.toLowerCase();
  const lowerContext = contextTerm.toLowerCase();

  // Extract a window around the match
  const windowSize = maxDistanceWords * 8; // rough chars-per-word estimate
  const windowStart = Math.max(0, patternIndex - windowSize);
  const windowEnd = Math.min(text.length, patternIndex + patternLength + windowSize);
  const window = lowerText.slice(windowStart, windowEnd);

  if (!window.includes(lowerContext)) return false;

  // Count words between pattern and context term
  const contextIdx = window.indexOf(lowerContext);
  const patternIdxInWindow = patternIndex - windowStart;
  
  const betweenStart = Math.min(contextIdx, patternIdxInWindow);
  const betweenEnd = Math.max(
    contextIdx + lowerContext.length,
    patternIdxInWindow + patternLength
  );
  const between = window.slice(betweenStart, betweenEnd);
  const wordCount = between.split(/\s+/).length;

  return wordCount <= maxDistanceWords;
}

// ============================================================================
// RULE EVALUATION
// ============================================================================

/**
 * Evaluate a single evidence rule against source text.
 */
function evaluateRule(
  text: string,
  source: EvidenceSource,
  rule: EvidenceRule,
): RuleMatchResult {
  const positiveSnippets: ProofEvidenceSnippet[] = [];
  const negativeSnippets: ProofEvidenceSnippet[] = [];

  // Search positive patterns
  for (const pattern of rule.patterns) {
    const hits = findPatternInText(text, pattern, source, rule.rule_id, 80, false);
    
    // If context required, filter hits
    if (rule.required_context && hits.length > 0) {
      const contextHits = hits.filter(hit => {
        const lowerText = text.toLowerCase();
        const patternIdx = lowerText.indexOf(pattern.toLowerCase());
        if (patternIdx === -1) return false;
        return checkContextProximity(
          text,
          patternIdx,
          pattern.length,
          rule.required_context!.term,
          rule.required_context!.max_distance_words,
        );
      });
      positiveSnippets.push(...contextHits);
    } else {
      positiveSnippets.push(...hits);
    }
  }

  // Search negative patterns
  for (const pattern of rule.negative_patterns) {
    const hits = findPatternInText(text, pattern, source, rule.rule_id, 80, true);
    negativeSnippets.push(...hits);
  }

  const hasContradiction = positiveSnippets.length > 0 && negativeSnippets.length > 0;
  const hitCount = positiveSnippets.length;
  const matched = hitCount >= rule.min_hits && negativeSnippets.length === 0;

  return {
    rule_id: rule.rule_id,
    matched: hasContradiction ? false : matched,
    hit_count: hitCount,
    confidence: rule.confidence,
    positive_snippets: deduplicateSnippets(positiveSnippets),
    negative_snippets: deduplicateSnippets(negativeSnippets),
    has_contradiction: hasContradiction,
    note: rule.note,
  };
}

/**
 * Deduplicate overlapping snippets.
 */
function deduplicateSnippets(snippets: ProofEvidenceSnippet[]): ProofEvidenceSnippet[] {
  if (snippets.length <= 1) return snippets;

  const sorted = [...snippets].sort((a, b) => a.offset_start - b.offset_start);
  const result: ProofEvidenceSnippet[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const previous = result[result.length - 1];

    if (current.offset_start <= previous.offset_end) {
      // Merge overlapping
      if (current.offset_end > previous.offset_end) {
        previous.offset_end = current.offset_end;
        previous.matched_text = previous.matched_text; // keep original for simplicity
      }
      current.matched_patterns.forEach(p => {
        if (!previous.matched_patterns.includes(p)) {
          previous.matched_patterns.push(p);
        }
      });
    } else {
      result.push(current);
    }
  }

  return result;
}

// ============================================================================
// REQUIREMENT EVALUATION
// ============================================================================

/**
 * Evaluate a single requirement element against source text.
 */
function evaluateRequirement(
  text: string,
  source: EvidenceSource,
  element: ProofMapRequirementElement,
): RequirementMatchResult {
  const ruleResults = element.evidence_rules.map(rule =>
    evaluateRule(text, source, rule)
  );

  const allSnippets = ruleResults.flatMap(r => [...r.positive_snippets, ...r.negative_snippets]);
  const anyMatched = ruleResults.some(r => r.matched);
  const anyContradiction = ruleResults.some(r => r.has_contradiction);
  const onlyLowConfidence = ruleResults
    .filter(r => r.matched)
    .every(r => r.confidence === 'LOW');

  // Determine status
  let status: RequirementStatus;
  if (anyContradiction) {
    status = 'AMBIGUOUS';
  } else if (anyMatched && onlyLowConfidence) {
    status = 'AMBIGUOUS';
  } else if (anyMatched) {
    status = 'SATISFIED';
  } else {
    status = 'NOT_FOUND';
  }

  // Best confidence
  const matchedConfidences = ruleResults
    .filter(r => r.matched)
    .map(r => r.confidence);
  const confidenceOrder: RuleConfidence[] = ['HIGH', 'MEDIUM', 'LOW'];
  const bestConfidence = confidenceOrder.find(c => matchedConfidences.includes(c)) ?? null;

  // Explanation
  let explanation: string;
  if (status === 'SATISFIED') {
    const matchedNotes = ruleResults.filter(r => r.matched).map(r => r.note);
    explanation = matchedNotes.join('; ');
  } else if (status === 'AMBIGUOUS') {
    if (anyContradiction) {
      explanation = 'Contradiction detected: both positive and negative evidence found';
    } else {
      explanation = 'Only low-confidence matches found — manual review recommended';
    }
  } else {
    explanation = `No evidence found for: ${element.label}`;
  }

  return {
    requirement_id: element.requirement_id,
    label: element.label,
    strictness: element.strictness,
    status,
    rule_results: ruleResults,
    all_snippets: deduplicateSnippets(allSnippets),
    has_contradiction: anyContradiction,
    best_confidence: bestConfidence,
    explanation,
  };
}

// ============================================================================
// CPT-LEVEL EVALUATION
// ============================================================================

/**
 * Determine overall documentation status for a CPT based on requirement results.
 */
function determineDocStatus(results: RequirementMatchResult[]): ProofDocStatus {
  const required = results.filter(r => r.strictness === 'REQUIRED');
  const anyAmbiguous = results.some(r => r.status === 'AMBIGUOUS');

  if (required.length === 0) {
    return anyAmbiguous ? 'AMBIGUOUS' : 'SUFFICIENT';
  }

  const allRequiredSatisfied = required.every(r => r.status === 'SATISFIED');
  const anyRequiredMissing = required.some(r => r.status === 'NOT_FOUND');
  const anyRequiredAmbiguous = required.some(r => r.status === 'AMBIGUOUS');

  if (allRequiredSatisfied && !anyAmbiguous) return 'SUFFICIENT';
  if (anyRequiredAmbiguous || anyAmbiguous) return 'AMBIGUOUS';
  if (anyRequiredMissing) return 'INSUFFICIENT';

  return 'AMBIGUOUS';
}

/**
 * Evaluate a complete proof map for one CPT against source text(s).
 */
export function evaluateCptProofMap(
  proofMap: CptProofMap,
  texts: Array<{ text: string; source: EvidenceSource }>,
): CptProofMapResult {
  // Evaluate all requirements against all text sources
  const requirementResults: RequirementMatchResult[] = proofMap.requirement_elements.map(element => {
    // Evaluate against each source and merge results
    const perSourceResults = texts.map(({ text, source }) =>
      evaluateRequirement(text, source, element)
    );

    // Merge: take best status across sources
    const mergedRuleResults = perSourceResults.flatMap(r => r.rule_results);
    const mergedSnippets = deduplicateSnippets(perSourceResults.flatMap(r => r.all_snippets));
    const anyContradiction = perSourceResults.some(r => r.has_contradiction);

    // Determine merged status
    const statuses = perSourceResults.map(r => r.status);
    let mergedStatus: RequirementStatus;
    if (statuses.includes('SATISFIED') && !anyContradiction) {
      mergedStatus = 'SATISFIED';
    } else if (anyContradiction || statuses.includes('AMBIGUOUS')) {
      mergedStatus = 'AMBIGUOUS';
    } else {
      mergedStatus = 'NOT_FOUND';
    }

    const bestConf = perSourceResults
      .map(r => r.best_confidence)
      .filter((c): c is RuleConfidence => c !== null);
    const confidenceOrder: RuleConfidence[] = ['HIGH', 'MEDIUM', 'LOW'];
    const best = confidenceOrder.find(c => bestConf.includes(c)) ?? null;

    const explanation = perSourceResults
      .filter(r => r.status === 'SATISFIED' || r.status === 'AMBIGUOUS')
      .map(r => r.explanation)
      .join('; ') || `No evidence found for: ${element.label}`;

    return {
      requirement_id: element.requirement_id,
      label: element.label,
      strictness: element.strictness,
      status: mergedStatus,
      rule_results: mergedRuleResults,
      all_snippets: mergedSnippets,
      has_contradiction: anyContradiction,
      best_confidence: best,
      explanation,
    };
  });

  const docStatus = determineDocStatus(requirementResults);
  const allSnippets = deduplicateSnippets(requirementResults.flatMap(r => r.all_snippets));

  return {
    cpt_code: proofMap.cpt_code,
    cpt_description: proofMap.cpt_description,
    documentation_status: docStatus,
    requirement_results: requirementResults,
    all_evidence_snippets: allSnippets,
    total_requirements: requirementResults.length,
    satisfied_count: requirementResults.filter(r => r.status === 'SATISFIED').length,
    missing_count: requirementResults.filter(r => r.status === 'NOT_FOUND').length,
    ambiguous_count: requirementResults.filter(r => r.status === 'AMBIGUOUS').length,
    has_any_contradiction: requirementResults.some(r => r.has_contradiction),
  };
}

// ============================================================================
// CASE-LEVEL ANALYSIS
// ============================================================================

/**
 * Run proof map analysis for all CPTs in a case.
 */
export function runCaseProofMapAnalysis(
  caseId: string,
  proofMaps: CptProofMap[],
  texts: Array<{ text: string; source: EvidenceSource }>,
): CaseProofMapResult {
  const cptResults = proofMaps.map(pm => evaluateCptProofMap(pm, texts));

  return {
    case_id: caseId,
    analyzed_at: new Date().toISOString(),
    sources_used: [...new Set(texts.map(t => t.source))],
    cpt_results: cptResults,
    total_cpts: cptResults.length,
    sufficient_count: cptResults.filter(r => r.documentation_status === 'SUFFICIENT').length,
    insufficient_count: cptResults.filter(r => r.documentation_status === 'INSUFFICIENT').length,
    ambiguous_count: cptResults.filter(r => r.documentation_status === 'AMBIGUOUS').length,
  };
}

// ============================================================================
// CLARIFICATION TRIGGERS
// ============================================================================

/**
 * Generate clarification triggers for missing/ambiguous requirements.
 */
export function generateClarificationTriggers(
  result: CaseProofMapResult,
): ProofMapClarificationTrigger[] {
  const triggers: ProofMapClarificationTrigger[] = [];

  for (const cptResult of result.cpt_results) {
    for (const reqResult of cptResult.requirement_results) {
      if (reqResult.strictness !== 'REQUIRED') continue;
      if (reqResult.status === 'SATISFIED') continue;

      const isContradiction = reqResult.has_contradiction;
      let promptText: string;

      if (isContradiction) {
        promptText = `Contradictory evidence found for CPT ${cptResult.cpt_code}: please confirm whether ${reqResult.label.toLowerCase()} was performed as documented.`;
      } else if (reqResult.status === 'AMBIGUOUS') {
        promptText = `To support CPT ${cptResult.cpt_code}, please confirm whether ${reqResult.label.toLowerCase()} was performed. Only low-confidence evidence was found.`;
      } else {
        promptText = `To support CPT ${cptResult.cpt_code}, please confirm whether ${reqResult.label.toLowerCase()} was performed and documented.`;
      }

      triggers.push({
        cpt_code: cptResult.cpt_code,
        requirement_id: reqResult.requirement_id,
        requirement_label: reqResult.label,
        status: reqResult.status === 'AMBIGUOUS' ? 'AMBIGUOUS' : 'NOT_FOUND',
        prompt_text: promptText,
        is_contradiction: isContradiction,
      });
    }
  }

  return triggers;
}
